import { Component, inject, ViewChild } from '@angular/core';
import {
    IComboSelectionChangingEventArgs,
    IDropDroppedEventArgs,
    IgxComboComponent,
    IgxSelectComponent,
    ISelectionEventArgs
} from '@infragistics/igniteui-angular';
import { BusStopType } from 'src/app/models/enums/busStopType';
import { UUID } from 'src/app/models/model';
import { MessagesService } from 'src/app/services/messages.service';
import { Bus, BusStop, Driver, Kid, TransportGroup, TransportService as TransportGroupService } from 'src/app/services/transport.service';
import { WaitIndicatorService } from 'src/app/services/wait-indicator.service';
import { Collection, Dates, MultiMap } from 'src/app/utils';

interface InternalGroup {
    bus: UUID;
    driver?: UUID;
    stops: MultiMap<UUID, Kid>;
}

@Component({
    selector: 'app-transport',
    templateUrl: './transport.component.html',
    styleUrls: ['./transport.component.scss']
})
export class TransportComponent {
    private _msgs = inject(MessagesService);
    private _waitIndicator = inject(WaitIndicatorService);
    private _service = inject(TransportGroupService);

    @ViewChild('busesCombo', { static: true, read: IgxComboComponent })
    private _busesCombo!: IgxComboComponent;

    /**
     * Directly correlates to the slots from which kids can be taken
     * Maps the ID of a stop to the list of kids there
     */
    private _stops = new MultiMap<UUID, Kid>(v => v.id);

    public date?: Date;

    public stops = Collection.empty<BusStop>();
    public buses = Collection.empty<Bus>();
    public kids = Collection.empty<Kid>();
    public drivers = Collection.empty<Driver>();
    public groups = new Map<UUID, InternalGroup>;

    private _addToBusInternal(kid: Kid | UUID, group: InternalGroup) {
        if (this.date == null) return;

        kid = this.kids.get(kid);
        if (kid.morningStop == null) return;

        for (const group of this.groups.values()) {
            if (group.stops.has(kid.morningStop, kid)) {
                group.stops.delete(kid.morningStop, kid);
                break;
            }
        }

        this._stops.delete(kid.morningStop, kid);
        group.stops.set(kid.morningStop, kid);
    }
    private _removeBusInternal(kid: Kid | UUID, group: InternalGroup) {
        kid = this.kids.get(kid);
        group.stops.delete(kid.morningStop, kid);
        this._stops.set(kid.morningStop, kid);
    }

    public async dateChange (e?: Date) {
        this._waitIndicator.show();

        this._stops = new MultiMap<UUID, Kid>(v => v.id);
        this.date = undefined;
        this.kids = Collection.empty();
        this.stops = Collection.empty();
        this.buses = Collection.empty();
        this.drivers = Collection.empty();
        this.groups = new Map();
        this._busesCombo.select([], true);
        this._waitIndicator.hide();

        e = Dates.correct(e);
        if (e == null) return;

        this.date = e;
        const { groups, ...res } = await this._service.load(this.date);

        this.kids = new Collection(res.kids);
        this.stops = new Collection(res.stops);
        this.buses = new Collection(res.buses);
        this.drivers = new Collection(res.drivers);

        const bucket = new Map<UUID, Kid>(this.kids.array().map(v => [v.id, v]));
        const buses = new Set<UUID>();

        for (const g of groups) {
            const group: InternalGroup = {
                bus: g.bus,
                driver: g.driver,
                stops: new MultiMap(v => v.id),
            }

            if (g.bus != null) buses.add(g.bus);

            for (const p of g.passengers) {
                const kid = bucket.get(p.kid);
                if (kid == null) throw new Error("Invalid group model");

                group.stops.set(p.stop, kid);
                bucket.delete(kid.id);
            }

            this.groups.set(group.bus, group);
        }

        for (const kid of bucket.values()) {
            this._stops.set(kid.morningStop, kid);
        }

        this._busesCombo?.select([...buses], true);
        this._waitIndicator.hide();
    }

    public busesChange(e: IComboSelectionChangingEventArgs) {
        const curr = new Map<UUID, Bus>(e.newSelection.map(v => [v.id, v]));

        for (const [el, group] of this.groups) {
            if (!curr.has(el)) {
                for (const kid of group.stops.values()) {
                    this._stops.set(kid.morningStop, kid);
                }
                this.groups.delete(group.bus);
            }
        }
        for (const [id, bus] of curr) {
            if (!this.groups.has(id)) {
                this.groups.set(id, {
                    bus: id,
                    driver: bus.driver,
                    stops: new MultiMap(),
                });
            }
        }
    }

    public selectedBuses() {
        return this.buses.getAll(this.groups.keys());
    }

    public kidOnStop(stop: BusStop) {
        const res = this._stops.get(stop.id);
        return res;
    }

    public kidOnBus(group: InternalGroup, stop?: BusStop) {
        if (stop == null) return new Set(group.stops.values());
        else return group.stops.get(stop.id);
    }

    public busCapacity(busId?: string) {
        return this.buses.get(busId)?.capacity ?? 0;
    }

    public occupiedSeatsOnBus(group: InternalGroup) {
        return this.kidOnBus(group).size;
    }

    public addToBus(e: IDropDroppedEventArgs, group: InternalGroup) {
        this._addToBusInternal(e.dragData.chip, group);
    }

    public removeKidFromBus(kid: Kid, group: InternalGroup) {
        this._removeBusInternal(kid.id!, group);
    }

    public moveAllKidsToBus(e: ISelectionEventArgs, stop: BusStop, select: IgxSelectComponent) {
        e.cancel = true;
        select.close();

        const kids = this.kidOnStop(stop);
        if (kids.size < 1) return;

        const group = this.groups.get(e.newSelection.value);
        if (group == null) throw new Error("Incorrect model");

        for (const k of kids) {
            this._addToBusInternal(k.id, group);
        }
    }

    public changeDriver(e: ISelectionEventArgs, group: InternalGroup) {
        group.driver = e.newSelection.value;
    }

    public async save() {
        try {
            if (this.date == null) throw new Error("No date");

            const groups: TransportGroup[] = [];

            for (const g of this.groups.values()) {
                const group: TransportGroup = {
                    bus: g.bus,
                    driver: g.driver,
                    passengers: [],
                };

                for (const [_, k] of g.stops) {
                    group.passengers.push({
                        kid: k.id,
                        stop: k.morningStop,
                        type: BusStopType.Morning,
                    });
                }

                groups.push(group);
            }

            await this._service.save(this.date, groups);
            this._msgs.success("Транспортната група е успешно записана");
        }
        catch (e) {
            this._msgs.error(e);
        }
    }

    public canSave() {
        return this._stops.size === 0 && this.date != null;
    }
}
